/**
 * Websocket controller
 *
 * To send messages use WebSocketController.send(msg)
 * To listen for events use AppEvents (messages are forwarded by event name).
 */

import { AppEvents } from "./app-events";
import { AuthController } from "./auth";

const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

export function getSocketUrl(): string {
    if (import.meta.env.DEV) {
        return (
            (location.protocol === "https:" ? "wss://" : "ws://") +
            new URL(import.meta.env.VITE__DEV_TEST_HOST || "http://localhost").host +
            "/websocket"
        );
    }
    return (location.protocol === "https:" ? "wss://" : "ws://") + location.host + "/websocket";
}

export class WebSocketController {
    public static ws: WebSocket | null = null;
    public static connected = false;
    public static hasAuthenticatedBefore = false;

    public static MAX_CONCURRENT_MESSAGES = 19;

    public static aliveTimeout: ReturnType<typeof setInterval> | null = null;
    public static reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    public static reconnectAttempt = 0;

    public static sentCounter = 0;
    public static queue: any[] = [];

    public static Initialize() {
        AppEvents.AddEventListener("auth-status-changed", WebSocketController.onAuthStatusChanged);
        WebSocketController.onAuthStatusChanged();
    }

    public static onAuthStatusChanged() {
        if (!AuthController.isAuthenticated()) {
            WebSocketController.disconnect();
            return;
        }

        WebSocketController.clearReconnectTimer();
        WebSocketController.reconnectAttempt = 0;

        if (!WebSocketController.ws) {
            WebSocketController.connect();
        } else if (WebSocketController.connected) {
            WebSocketController.authenticate();
        }
    }

    /* Connection */

    public static connect() {
        if (!AuthController.isAuthenticated() || WebSocketController.ws) {
            return;
        }

        WebSocketController.ws = new WebSocket(getSocketUrl());
        WebSocketController.aliveTimeout = setInterval(WebSocketController.alive, 10000);

        WebSocketController.ws.onopen = function () {
            WebSocketController.connected = true;
            WebSocketController.sentCounter = 0;
            WebSocketController.queue = [];
            WebSocketController.reconnectAttempt = 0;
            WebSocketController.authenticate();
        };

        WebSocketController.ws.onclose = function () {
            WebSocketController.ws = null;
            WebSocketController.connected = false;
            WebSocketController.clearAliveInterval();

            // Logout clears session before disconnect; only retry while logged in.
            if (AuthController.isAuthenticated()) {
                WebSocketController.scheduleReconnect();
            } else {
                WebSocketController.reconnectAttempt = 0;
            }
        };

        WebSocketController.ws.onerror = function (err) {
            console.error(err);
        };

        WebSocketController.ws.addEventListener("message", function (msg) {
            try {
                WebSocketController.onMessage(JSON.parse("" + msg.data));
            } catch (err) {
                console.error(err);
            }
        });
    }

    public static disconnect() {
        WebSocketController.clearReconnectTimer();
        WebSocketController.reconnectAttempt = 0;
        WebSocketController.hasAuthenticatedBefore = false;
        WebSocketController.queue = [];
        WebSocketController.sentCounter = 0;
        WebSocketController.clearAliveInterval();

        if (WebSocketController.ws) {
            WebSocketController.ws.close();
        } else {
            WebSocketController.connected = false;
        }
    }

    public static scheduleReconnect() {
        if (!AuthController.isAuthenticated()) {
            return;
        }

        WebSocketController.clearReconnectTimer();

        const delay = Math.min(BASE_RECONNECT_DELAY_MS * Math.pow(2, WebSocketController.reconnectAttempt), MAX_RECONNECT_DELAY_MS);
        WebSocketController.reconnectAttempt++;

        WebSocketController.reconnectTimer = setTimeout(() => {
            WebSocketController.reconnectTimer = null;
            if (AuthController.isAuthenticated() && !WebSocketController.ws) {
                WebSocketController.connect();
            }
        }, delay);
    }

    public static clearReconnectTimer() {
        if (WebSocketController.reconnectTimer) {
            clearTimeout(WebSocketController.reconnectTimer);
            WebSocketController.reconnectTimer = null;
        }
    }

    public static clearAliveInterval() {
        if (WebSocketController.aliveTimeout) {
            clearInterval(WebSocketController.aliveTimeout);
            WebSocketController.aliveTimeout = null;
        }
    }

    /* Send */

    public static send(msg: any) {
        if (WebSocketController.ws && WebSocketController.connected) {
            if (WebSocketController.sentCounter >= WebSocketController.MAX_CONCURRENT_MESSAGES) {
                WebSocketController.queue.push(msg);
                return;
            }

            WebSocketController.ws.send(JSON.stringify(msg));
            WebSocketController.sentCounter++;

            if (WebSocketController.sentCounter >= WebSocketController.MAX_CONCURRENT_MESSAGES) {
                WebSocketController.ws.send(JSON.stringify({ type: "promise" }));
            }
        }
    }

    /* Queue */

    public static releaseQueue() {
        const prevQueue = WebSocketController.queue;
        WebSocketController.queue = [];
        prevQueue.forEach((m) => WebSocketController.send(m));
    }

    /* Keep alive */

    public static alive() {
        if (WebSocketController.ws && WebSocketController.connected) {
            WebSocketController.ws.send("a");
        }
    }

    /* Authentication */

    public static authenticate() {
        if (AuthController.isAuthenticated()) {
            WebSocketController.send({
                type: "auth",
                session: AuthController.Session,
            });
        }
    }

    /* On message */

    public static onMessage(message: any) {
        switch (message.event) {
            case "hello":
                break;
            case "resolved":
                WebSocketController.sentCounter = 0;
                WebSocketController.releaseQueue();
                break;
            case "auth-success":
                AppEvents.Emit("websocket-authenticated", WebSocketController.hasAuthenticatedBefore);
                WebSocketController.hasAuthenticatedBefore = true;
                break;
            case "auth-error":
                break;
            default:
                break;
        }
        if (message.event) {
            AppEvents.Emit(message.event as any, message);
        }
    }
}
